import { useEffect, useRef } from 'react';
import { Biome } from '@settlers/shared';
import type { GameClient } from '../game/GameClient.js';

const SIZE = 200;

const BIOME_COLOR: Readonly<Record<Biome, string>> = {
  [Biome.Ocean]: '#204a6e',
  [Biome.Lake]: '#3a78a0',
  [Biome.Beach]: '#d3bd82',
  [Biome.Meadow]: '#7fa653',
  [Biome.Forest]: '#436b32',
  [Biome.Mountain]: '#8b8681',
  [Biome.Snow]: '#e6edf2',
  [Biome.Swamp]: '#566345',
  [Biome.Desert]: '#cbb06a',
};

/** Full-map overview with a live viewport indicator and click-to-navigate. */
export const Minimap = ({ client }: { client: GameClient | null }): React.JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const baseRef = useRef<HTMLCanvasElement | null>(null);

  // Bake the static biome image whenever a new world is generated.
  useEffect(() => {
    if (!client) return;
    const map = client.gameMap;
    const base = document.createElement('canvas');
    base.width = map.width;
    base.height = map.height;
    const bctx = base.getContext('2d');
    if (!bctx) return;
    const img = bctx.createImageData(map.width, map.height);
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const color = BIOME_COLOR[map.biomeAt(x, y)];
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        const o = (y * map.width + x) * 4;
        img.data[o] = r;
        img.data[o + 1] = g;
        img.data[o + 2] = b;
        img.data[o + 3] = 255;
      }
    }
    bctx.putImageData(img, 0, 0);
    baseRef.current = base;
  }, [client]);

  // Live overlay: draw base + viewport quad each frame.
  useEffect(() => {
    if (!client) return;
    let raf = 0;
    const draw = (): void => {
      const canvas = canvasRef.current;
      const base = baseRef.current;
      if (canvas && base) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const map = client.gameMap;
          ctx.imageSmoothingEnabled = false;
          ctx.clearRect(0, 0, SIZE, SIZE);
          ctx.drawImage(base, 0, 0, SIZE, SIZE);

          const quad = client.viewportGridQuad();
          ctx.beginPath();
          quad.forEach((p, i) => {
            const px = (p.x / map.width) * SIZE;
            const py = (p.y / map.height) * SIZE;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          });
          ctx.closePath();
          ctx.strokeStyle = 'rgba(255,255,255,0.9)';
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.fillStyle = 'rgba(255,255,255,0.08)';
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return (): void => cancelAnimationFrame(raf);
  }, [client]);

  const onClick = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    if (!client) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const map = client.gameMap;
    const gx = ((e.clientX - rect.left) / rect.width) * map.width;
    const gy = ((e.clientY - rect.top) / rect.height) * map.height;
    client.moveToGrid(gx, gy);
  };

  return (
    <div className="panel minimap">
      <div className="panel-title">Minimap</div>
      <canvas
        ref={canvasRef}
        width={SIZE}
        height={SIZE}
        className="minimap-canvas"
        onClick={onClick}
      />
    </div>
  );
};
